import { useCallback, useEffect, useState } from 'react'
import { useVerifyTokenMutation, useUserLoginMutation as useCrsLoginMutation } from '@client-hub/store/api/crs/react'
import { useSelector } from 'react-redux'
import { selectCrsToken } from '@client-hub/store/slices/metadata/selectors'
import { RootState } from '@client-hub/store/createStore'
import { CRS_LOGIN_MUTATION_CACHE_KEY } from 'constants/auth'

type ApiFnHook<T = Record<string, string> | void> = (
  params: T,
) => [() => any, { isLoading: boolean; isSuccess: boolean; isError: boolean }]

type TokenValidationResponse = [boolean | undefined, { isLoading: boolean }]

const composeTokenValidationHook =
  (
    tokenSelector: (state: RootState) => string | undefined,
    apiValidationFnHook: ApiFnHook,
    apiAuthFnHook: ApiFnHook<{ fixedCacheKey: string }>,
    fixedCacheKey: string,
  ) =>
  (): TokenValidationResponse => {
    const [, { isSuccess: authSuccess }] = apiAuthFnHook({ fixedCacheKey })
    const token = useSelector(tokenSelector)
    const [isChecked, setIsChecked] = useState(!token || authSuccess)
    const [verifyToken, { isLoading, isSuccess, isError }] = apiValidationFnHook()
    // Below variable tracks whether the user logged in on this "session" meaning
    // that token validation doesn't need to occur
    // TODO: This will eventually mean we need to introduced checks for 4xx/5xx errors
    // in a redux middleware for long living sessions (user leaves the page open for longer
    // than token lifetime).
    const isAuthenticated = authSuccess || isSuccess
    const [sessionAuthenticated, setSessionAuthenticated] = useState(isAuthenticated)
    useEffect(() => {
      if (token && !sessionAuthenticated) verifyToken()
    }, [token, sessionAuthenticated])
    useEffect(() => {
      if (isError || isSuccess) setIsChecked(true)
    }, [isError, isSuccess])
    useEffect(() => {
      if (authSuccess && !sessionAuthenticated) {
        setSessionAuthenticated(true)
        setIsChecked(true)
      }
    }, [authSuccess, sessionAuthenticated])

    useEffect(() => {
      setSessionAuthenticated((prev) => prev || isAuthenticated)
    }, [isAuthenticated])

    let tokenValid
    if (!token || isError) {
      tokenValid = false
    } else if (!isLoading && (isSuccess || sessionAuthenticated)) {
      tokenValid = true
    }
    return [tokenValid, { isLoading: isLoading || !isChecked }]
  }

export const useHubTokenValidation = composeTokenValidationHook(
  selectCrsToken,
  () => {
    const [verifyToken, metadata] = useVerifyTokenMutation()
    const token = useSelector(selectCrsToken)
    const handleVerifyToken = useCallback(() => verifyToken({ token: token! }), [token])
    return [handleVerifyToken, metadata]
  },
  useCrsLoginMutation as ApiFnHook,
  CRS_LOGIN_MUTATION_CACHE_KEY,
)
